import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button } from '@mui/material';
import apiMcu from 'api/mcu';
import { SearchCC } from 'components/common/SearchCC';
import { DIALOG_ID } from 'components/Dialog/constant';
import LoadingBackdrop from 'components/Layout/Loading/LoadingBackdrop';
import SearchPage from 'components/Layout/SearchPage';
import { FUNCTIONS } from 'constants/roles';
import { useResponsiveCC, useTableCC } from 'hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setDetailDevice, setIsDetailed } from 'store/mcuSlice';
import { checkFunctionHaveCode } from 'store/user/functions';
import { selectCurrentUserFunctions } from 'store/selectors';
import theme from 'theme';
import { setCloseDialogCC, setOpenDialogCC, toastError, toastSuccess } from 'utils/helpers';
import { DetailDevice } from './components/DetailDevice';
import { DeviceList } from './DeviceList';
import { MCUControlPage } from './MCUControlPage';
import TabMCUControl from './TabMCUControl';
import { ContainerPage, BreadcrumbsCC } from 'components/Layout';
import { IconClose } from 'assets/icons';

const MCUControl = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { isLargePC } = useResponsiveCC();
    const { setCurrentPage, handleChangePage, sizeRow, currentPage } = useTableCC();
    const [dataLake, setDataLake] = useState([]);
    const [roomNameOrRoomCode, setRoomNameOrRoomCode] = useState('');
    const [reload, setReload] = useState(false);
    const [loading, setLoading] = useState(false);
    const functions = useSelector(selectCurrentUserFunctions);
    const tabMCU = useSelector((state) => state.mcu.searchQuery.tab);
    const isDetailDevice = useSelector((state) => state.mcu.isDetailed);
    const dispatch = useDispatch();

    const apiGetListRooms = (roomNameOrRoomCode, currentPage) => {
        return apiMcu.getListRooms(roomNameOrRoomCode, currentPage);
    };

    const apiGetListDevices = (roomNameOrRoomCode, currentPage) => {
        return apiMcu.getListDevices({ page: currentPage, codeOrName: roomNameOrRoomCode })
    }

    const apiDeleteRoom = (id) => {
        return apiMcu.deleteRoom(id);
    };

    const handleSearchRoom = () => {
        setCurrentPage(0)
        setReload((prev) => !prev);
    };

    const handleOpenDialogDelete = (name, id) => {
        const content = <div>
            {t('mcu.dialog.content.deleteDevice')} <span style={{ fontWeight: 700 }}>{name}</span>?
        </div>
        setOpenDialogCC({
            id: DIALOG_ID.CONFIRM,
            props: {
                title: t("mcu.dialog.title.deleteRoom"),
                contentText: content,
                isDisableLoadingButton: true,
                cancel: t('mcu.dialog.action.cancel'),
                action: t('mcu.dialog.action.deleteRoom'),
                onClick: () => {
                    apiDeleteRoom(id)
                        .then(result => {
                            toastSuccess(t('mcu.toast.deleteRoomSuccessfully'))
                            setReload(prev => !prev);
                        })
                        .catch(error => {
                            if (error?.response?.data?.code === "ROOM_IN_PROGRESS") {
                                toastError(t('mcu.toast.errorDeleteRoom'))
                            } else {
                                toastError(t('mcu.toast.error'))
                            }
                        })
                        .finally(() => {
                            setCloseDialogCC();
                        })
                }
            }
        })
    }

    useEffect(() => {
        setLoading(true);
        if (tabMCU === 0) {
            apiGetListRooms(roomNameOrRoomCode, currentPage)
                .then((result) => {
                    setDataLake(result);
                })
                .catch((error) => {
                    toastError(t('mcu.toast.error'))
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            apiGetListDevices(roomNameOrRoomCode, currentPage)
                .then((result) => {
                    setDataLake(result);
                })
                .catch((error) => {
                    toastError(t('mcu.toast.error'))
                })
                .finally(() => {
                    setLoading(false);
                });
        }
        // eslint-disable-next-line
    }, [reload, currentPage]);

    useEffect(() => {
        setReload(prev => !prev);
        setCurrentPage(0);
        dispatch(setIsDetailed(false));
        dispatch(setDetailDevice({}));

        return () => {
            dispatch(setIsDetailed(false));
            dispatch(setDetailDevice({}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabMCU])

    return (
        <ContainerPage>
            <LoadingBackdrop open={loading} />
            <BreadcrumbsCC>
                {
                    checkFunctionHaveCode(functions, FUNCTIONS.MCU_CONTROL_ROOM_EDIT)
                    &&
                    tabMCU === 0
                    &&
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={
                            <AddCircleOutlineIcon
                                sx={{ color: 'white', width: theme.spacing(2.5), height: theme.spacing(2.5) }}
                            />
                        }
                        sx={{ marginLeft: '16px' }}
                        onClick={() => {
                            setOpenDialogCC({
                                id: DIALOG_ID.CREATE_MCU_ROOM,
                                props: {
                                    title: t('mcu.dialog.title.addRoom'),
                                    titleButtonAction: t('mcu.dialog.action.addRoom'),
                                    titleButtonCancel: t('mcu.dialog.action.cancel'),
                                    agreeAction: () => {
                                        setCurrentPage(0);
                                        setReload((prev) => !prev);
                                    },
                                },
                            });
                        }}
                    >
                        <div style={{ fontWeight: '700' }}>{t('mcu.button.createRoom')}</div>
                    </Button>
                }
                {
                    tabMCU === 1
                    &&
                    isDetailDevice === false
                    &&
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={
                            <AddCircleOutlineIcon
                                sx={{ color: 'white', width: theme.spacing(2.5), height: theme.spacing(2.5) }}
                            />
                        }
                        sx={{ marginLeft: '16px' }}
                        onClick={() => {
                            setOpenDialogCC({
                                id: DIALOG_ID.CREATE_OR_UPDATE_MCU_DEVICE,
                                props: {
                                    title: t('mcu.dialog.title.addDevice'),
                                    titleButtonAction: t('mcu.dialog.action.addDevice'),
                                    titleButtonCancel: t('mcu.dialog.action.cancel'),
                                    agreeAction: () => {
                                        setCurrentPage(0);
                                        setReload((prev) => !prev);
                                    },
                                },
                            });
                        }}
                    >
                        <div style={{ fontWeight: '700' }}>{t('mcu.button.addDevice')}</div>
                    </Button>
                }

            </BreadcrumbsCC>
            <SearchPage>
                <TabMCUControl t={t} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    {
                        (isDetailDevice && tabMCU === 1)
                            ?
                            <Button
                                className='stroke'
                                sx={{backgroundColor: '#ffff'}}
                                variant="outlined"
                                size="small"
                                startIcon={<IconClose />}
                                onClick={() => {
                                    dispatch(setIsDetailed(false));
                                    dispatch(setDetailDevice({}));
                                }}
                            >
                                <div style={{ fontWeight: '700' }}>{t('actions.closePage')}</div>
                            </Button>
                            :
                            <SearchCC
                                placeholder={tabMCU === 0 ? 'mcu.placeholder.enterRoomName' : 'mcu.placeholder.enterDevice'}
                                backgroundColor={'#FFFF'}
                                value={roomNameOrRoomCode}
                                setValue={setRoomNameOrRoomCode}
                                handleValue={(value) => {
                                    handleSearchRoom(value);
                                }}
                            />
                    }
                </Box>
            </SearchPage>
            <Box sx={{ margin: '24px 20px 0 20px' }}>
                {
                    tabMCU === 0 ?
                        <MCUControlPage
                            sizeRow={sizeRow}
                            dataLake={dataLake}
                            functions={functions}
                            t={t}
                            setReload={setReload}
                            handleOpenDialogDelete={handleOpenDialogDelete}
                            currentPage={currentPage}
                            handleChangePage={handleChangePage}
                            isLargePC={isLargePC}
                            navigate={navigate}
                        />
                        :
                        (
                            !isDetailDevice
                                ?
                                <DeviceList
                                    sizeRow={sizeRow}
                                    dataLake={dataLake}
                                    t={t}
                                    setReload={setReload}
                                    handleOpenDialogDelete={handleOpenDialogDelete}
                                    currentPage={currentPage}
                                    handleChangePage={handleChangePage}
                                    isLargePC={isLargePC}
                                    navigate={navigate}
                                />
                                :
                                <DetailDevice t={t} />
                        )
                }
            </Box>
        </ContainerPage>
    );
};

export default MCUControl;
